/**
 * @fileoverview Documents Tab for Institution Central
 * Displays institution documents with status and actions
 */

import React, { useState, useMemo } from "react";
import { formatDate } from '../../../utils/formatters';
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    IconButton,
    Alert,
    Button,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from "@mui/material";
import { intl, getToken, Notifications } from "dmsbasereact";
import { getBaseURL } from "../../../build_config";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import SendIcon from "@mui/icons-material/Send";

import { DmsFileViewerModal } from "../../../components/DmsFileViewer";
import { getTranslatedDocumentName } from "../../../utils/documentTranslations";
import { DocumentStatusEnum, DocumentStatus } from "../../../constants/DocumentStatus";
import { isExpired, isExpiringSoon } from "../../../utils/expiration";
import { InstitutionStatusEnum } from "../../../constants/InstitutionStatus";

/**
 * Get document status info based on expiration date.
 * Documents are NOT individually approved/rejected - they are items
 * required to evaluate the institution. Status is based on validity date.
 */
const getDocumentStatusInfo = (doc) => {
    // Check if expired (by status or by date)
    if (doc.status === DocumentStatusEnum.EXPIRED || isExpired(doc.expiration_date)) {
        return {
            icon: <ErrorIcon fontSize="small" />,
            color: DocumentStatus.getColor(DocumentStatusEnum.EXPIRED),
            label: DocumentStatus.getText(DocumentStatusEnum.EXPIRED)
        };
    }
    // Check if expiring soon
    if (isExpiringSoon(doc.expiration_date)) {
        return {
            icon: <WarningIcon fontSize="small" />,
            color: "warning",
            label: intl.get("ExpiringSoon").d("Expiring Soon")
        };
    }
    // Document is valid
    return {
        icon: <CheckCircleIcon fontSize="small" />,
        color: DocumentStatus.getColor(DocumentStatusEnum.APPROVED),
        label: DocumentStatus.getText(DocumentStatusEnum.APPROVED)
    };
};

/**
 * DocumentsTab - Displays institution documents
 */
export const DocumentsTab = ({
  documents = [],
  institution = null,
  onEdit = null,
  onSubmitForApproval = null,
  submitting = false
}) => {
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const institutionStatus = institution?.status;
    const isPendingDocumentUpload = institutionStatus === InstitutionStatusEnum.PENDING_DOCUMENT_UPLOAD;
    const isRejected = institutionStatus === InstitutionStatusEnum.REJECTED;

    // Count documents by validity status (based on expiration date)
    const expiredCount = documents.filter(d =>
        d.status === DocumentStatusEnum.EXPIRED || isExpired(d.expiration_date)
    ).length;
    const expiringSoonCount = documents.filter(d =>
        d.status !== DocumentStatusEnum.EXPIRED &&
        !isExpired(d.expiration_date) &&
        isExpiringSoon(d.expiration_date)
    ).length;

    const hasAlerts = expiredCount > 0 || expiringSoonCount > 0;

    // Check if at least 1 document is uploaded (required for submission)
    const hasAtLeastOneDocument = useMemo(() => {
        if (!documents || documents.length === 0) {
            return false;
        }
        return documents.some(doc => doc.file_pk);
    }, [documents]);

    const handleViewFile = (doc) => {
        const filePk = doc.file_pk || doc.file?.pk;
        if (!filePk) {
            console.error("No file_pk found for document:", doc);
            Notifications.toast(
                intl.get("NoFileToView").d("No file available to view"),
                Notifications.TYPE_WARNING
            );
            return;
        }
        setSelectedDocument({
            filePk,
            fileName: doc.original_filename || doc.file?.original_filename || doc.document_name || doc.name || 'document.pdf'
        });
        setViewerOpen(true);
    };

    const handleCloseViewer = () => {
        setViewerOpen(false);
        setSelectedDocument(null);
    };

    const handleDownload = async (doc) => {
        const filePk = doc.file_pk || doc.file?.pk;
        const fileName = doc.original_filename || doc.file?.original_filename || doc.document_name || doc.name || 'document.pdf';

        if (!filePk) return;

        try {
            const token = getToken();
            const response = await fetch(`${getBaseURL()}/file/${filePk}`, {
                headers: {
                    'Authorization': `Bearer ${token?.access_token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to download file');
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Failed to download file:", err);
            Notifications.toast(
                intl.get("FailedToDownloadFile").d("Failed to download file"),
                Notifications.TYPE_ERROR
            );
        }
    };

    const handleSubmitClick = () => {
        if (documents.length === 0) {
            setConfirmDialogOpen(true);
        } else {
            onSubmitForApproval();
        }
    };

    const handleConfirmSubmitWithoutDocs = () => {
        setConfirmDialogOpen(false);
        onSubmitForApproval();
    };

    return (
        <Box>
            {/* Alerts for document validity */}
            {hasAlerts && (
                <Box sx={{ mb: 2 }}>
                    {expiredCount > 0 && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                            {intl.get("ExpiredDocumentsAlert", { count: expiredCount })
                                .d("{count} documento(s) expirado(s) - precisam ser atualizados")}
                        </Alert>
                    )}
                    {expiringSoonCount > 0 && (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            {intl.get("ExpiringSoonDocumentsAlert", { count: expiringSoonCount })
                                .d("{count} documento(s) expirando em breve")}
                        </Alert>
                    )}
                </Box>
            )}

            {/* Documents List */}
            {documents.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary" gutterBottom>
                        {intl.get("NoDocumentsUploaded").d("No documents uploaded")}
                    </Typography>
                    {onEdit && (
                        <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={onEdit}
                            sx={{ mt: 2 }}
                        >
                            {intl.get("UploadDocuments").d("Upload Documents")}
                        </Button>
                    )}

                    {/* Submit for Approval without documents */}
                    {(isPendingDocumentUpload || isRejected) && onSubmitForApproval && (
                        <Alert
                            severity="warning"
                            sx={{ mt: 3, textAlign: "left" }}
                        >
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                {isRejected
                                    ? intl.get("Institution.Rejected.Title").d("Institution Rejected")
                                    : intl.get("Institution.PendingDocuments.Title").d("Upload Documents and Submit for Approval")
                                }
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {intl.get("Institution.SubmitWithoutDocs.Info").d(
                                    "You can submit for approval now and add documents later. However, your final approval will depend on providing all required documentation."
                                )}
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                color="warning"
                                startIcon={<SendIcon />}
                                onClick={handleSubmitClick}
                                disabled={submitting || !hasAtLeastOneDocument}
                            >
                                {submitting
                                    ? intl.get("Submitting").d("Submitting...")
                                    : isRejected
                                        ? intl.get("ResubmitForApproval").d("Resubmit for Approval")
                                        : intl.get("SubmitForApproval").d("Submit for Approval")
                                }
                            </Button>
                        </Alert>
                    )}
                </Box>
            ) : (
                <>
                    <List disablePadding>
                        {documents.map((doc, index) => {
                            const status = getDocumentStatusInfo(doc);
                            const filePk = doc.file_pk || doc.file?.pk;
                            return (
                                <ListItem
                                    key={doc.pk || index}
                                    divider={index < documents.length - 1}
                                    sx={{ px: 0 }}
                                >
                                    <ListItemText
                                        primary={getTranslatedDocumentName(doc.document_name || doc.name)}
                                        secondary={
                                            doc.expiration_date
                                                ? `${intl.get("Expires").d("Expires")}: ${formatDate(doc.expiration_date)}`
                                                : null
                                        }
                                        primaryTypographyProps={{ variant: "body2" }}
                                        secondaryTypographyProps={{ variant: "caption" }}
                                    />
                                    <ListItemSecondaryAction>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            {filePk && (
                                                <>
                                                    <Tooltip title={intl.get("Tooltip.Action.View").d("View")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleViewFile(doc);
                                                            }}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={intl.get("Tooltip.Action.Download").d("Download")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDownload(doc);
                                                            }}
                                                        >
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            <Chip
                                                label={status.label}
                                                color={status.color}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </Box>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            );
                        })}
                    </List>

                    {/* Submit for Approval Section */}
                    {(isPendingDocumentUpload || isRejected) && onSubmitForApproval && (
                        <Alert
                            severity={isRejected ? "warning" : "info"}
                            icon={isRejected ? <WarningIcon /> : undefined}
                            sx={{ mt: 3, mb: 2 }}
                        >
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                {isRejected
                                    ? intl.get("Institution.Rejected.Title").d("Institution Rejected")
                                    : intl.get("Institution.PendingDocuments.Title").d("Upload Documents and Submit for Approval")
                                }
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {isRejected
                                    ? intl.get("Institution.Rejected.Instructions").d(
                                        "Review your institution's information and documents, make the necessary corrections, and resubmit for a new review."
                                    )
                                    : intl.get("Institution.PendingDocuments.Instructions").d(
                                        "Upload all required documents in the Documents tab, then click the button below to submit your institution for approval."
                                    )
                                }
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<SendIcon />}
                                onClick={handleSubmitClick}
                                disabled={submitting}
                            >
                                {submitting
                                    ? intl.get("Submitting").d("Submitting...")
                                    : isRejected
                                        ? intl.get("ResubmitForApproval").d("Resubmit for Approval")
                                        : intl.get("SubmitForApproval").d("Submit for Approval")
                                }
                            </Button>
                            {documents.length === 0 && (
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                    {intl.get("DocumentsRequiredForApproval").d("Documents will be required for final approval")}
                                </Typography>
                            )}
                        </Alert>
                    )}

                    {/* Edit Button */}
                    {onEdit && (
                        <Box sx={{ mt: 3 }}>
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={onEdit}
                                fullWidth
                            >
                                {intl.get("ManageDocuments").d("Manage Documents")}
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {/* Document Viewer Modal */}
            <DmsFileViewerModal
                isOpen={viewerOpen}
                onClose={handleCloseViewer}
                filePk={selectedDocument?.filePk}
                fileName={selectedDocument?.fileName}
            />

            {/* Confirmation Dialog for Submit Without Documents */}
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
            >
                <DialogTitle>
                    {intl.get("ConfirmSubmitWithoutDocuments.Title").d("Submit Without Documents?")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {intl.get("ConfirmSubmitWithoutDocuments.Message").d(
                            "You are submitting your institution for approval without uploading any documents. " +
                            "Please note that you will need to add the required documents later, and your final approval " +
                            "in the system will depend on providing all necessary documentation."
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>
                        {intl.get("Cancel").d("Cancel")}
                    </Button>
                    <Button
                        onClick={handleConfirmSubmitWithoutDocs}
                        variant="contained"
                        color="warning"
                    >
                        {intl.get("ConfirmSubmitWithoutDocuments.Confirm").d("Submit Anyway")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

DocumentsTab.propTypes = {
    documents: PropTypes.array,
    institution: PropTypes.object,
    onEdit: PropTypes.func,
    onSubmitForApproval: PropTypes.func,
    submitting: PropTypes.bool
};

